import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { AppProvider } from './context/AppContext';
import { CalendarProvider } from './context/CalendarContext';
import { PWAUpdateProvider } from './context/PWAUpdateContext';
import { installGlobalErrorHandlers } from './lib/error-reporting';
import { bootAction } from './lib/server-path';

// Catches what React error boundaries structurally cannot: throws from event
// handlers and timers, and promise rejections nobody awaited. Installed before
// React mounts so a failure during boot is still reported.
installGlobalErrorHandlers();

// Google Analytics is initialized in index.html

const RECLAIM_FLAG = 'trackmind:server-path-reclaimed';

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RECLAIM_FLAG) === '1';
  } catch {
    // Private mode, or storage disabled. Treat as "not yet tried" — one
    // reload attempt is the right call, and without the flag we'd rather
    // fall through to the explanation than loop, so mark it below too.
    return false;
  }
}

function markFlag(): void {
  try {
    sessionStorage.setItem(RECLAIM_FLAG, '1');
  } catch {
    // Nothing to do. The reload below still happens once; if storage is
    // unavailable the worst case is a second attempt.
  }
}

/**
 * A stale service worker answered a server path from cache. Drop every worker
 * registered for this origin and reload, so the request actually reaches the
 * Netlify function this time.
 */
async function reclaimServerPath(): Promise<void> {
  markFlag();
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.() ?? [];
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Reload anyway — an uncontrolled retry may still win.
  }
  window.location.reload();
}

function explainServerPath(): void {
  const root = document.getElementById('root');
  if (root) {
    root.textContent =
      'This address belongs to the TrackMind server, not the app. '
      + 'An out-of-date offline cache is intercepting it. Please close all '
      + 'TrackMind tabs and open the app again, then retry.';
  }
}

const action = bootAction(window.location.pathname, readFlag());

if (action === 'reclaim') {
  void reclaimServerPath();
} else if (action === 'explain') {
  explainServerPath();
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <PWAUpdateProvider>
        <AppProvider>
          <CalendarProvider>
            <App />
          </CalendarProvider>
        </AppProvider>
      </PWAUpdateProvider>
    </StrictMode>,
  );
}
